import React, { useState, useRef, useEffect } from 'react'
import { useLanguage, LANGUAGES } from '../context/LanguageContext'

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

/**
 * LanguageSwitcher
 * Globe-icon dropdown supporting all 10 languages (A–Z).
 *
 * Props:
 *   variant  – 'light' (white text, for dark nav bars)
 *              'dark'  (dark text, for light backgrounds)
 *   dropUp   – true = dropdown opens upward (use in bottom sidebars)
 *              false (default) = opens downward (use in top nav bars)
 *   style    – extra inline style overrides
 */
export default function LanguageSwitcher({ variant = 'light', dropUp = false, style = {} }) {
  const { lang, switchLang } = useLanguage()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const current  = LANGUAGES.find(l => l.code === lang) || LANGUAGES.find(l => l.code === 'en')
  const isLight  = variant === 'light'
  const btnColor = isLight ? 'rgba(255,255,255,0.9)' : '#374151'
  const btnBg    = isLight ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.05)'
  const btnHover = isLight ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.1)'

  // Groups in A–Z order
  const groups = [
    { label: 'English',           codes: ['en'] },
    { label: 'Top US Languages',  codes: ['ar', 'zh', 'fr', 'ko', 'es', 'tl', 'vi'] },
    { label: 'Southeast Asia',    codes: ['id', 'th'] },
  ]

  const dropStyle = dropUp
    ? { bottom: 'calc(100% + 8px)', top: 'auto' }
    : { top:    'calc(100% + 8px)', bottom: 'auto' }

  return (
    <div ref={ref} style={{ position: 'relative', ...style }} dir="ltr">
      <button
        onClick={() => setOpen(o => !o)}
        title="Change language / Cambiar idioma / 更改语言"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 11px', borderRadius: 8, border: 'none',
          background: open ? btnHover : btnBg,
          color: btnColor, cursor: 'pointer',
          fontSize: 13, fontWeight: 500,
          fontFamily: FONT,
          transition: 'background .15s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => e.currentTarget.style.background = btnHover}
        onMouseLeave={e => e.currentTarget.style.background = open ? btnHover : btnBg}
      >
        {/* Globe icon */}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
        </svg>
        <span>{current.flag} {current.nativeLabel}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          left: 0,
          ...dropStyle,
          background: 'white',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          minWidth: 230,
          maxHeight: 440,
          overflowY: 'auto',
          zIndex: 9999,
          direction: 'ltr',
        }}>
          <style>{`
            @keyframes langFade {
              from { opacity: 0; transform: translateY(${dropUp ? '6px' : '-6px'}); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .lang-dd { animation: langFade .15s ease; }
            .lang-dd::-webkit-scrollbar { width: 4px; }
            .lang-dd::-webkit-scrollbar-track { background: #f9fafb; }
            .lang-dd::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
          `}</style>

          <div className="lang-dd">
            {groups.map((group, gi) => (
              <div key={group.label}>
                <div style={{
                  padding: '8px 14px 4px',
                  fontSize: 10, fontWeight: 600,
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontFamily: FONT,
                  background: '#f9fafb',
                  borderTop: gi > 0 ? '1px solid #f3f4f6' : 'none',
                }}>
                  {group.label}
                </div>

                {group.codes.map(code => {
                  const lng    = LANGUAGES.find(l => l.code === code)
                  if (!lng) return null
                  const active = lang === code
                  return (
                    <button
                      key={code}
                      onClick={() => { switchLang(code); setOpen(false) }}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 14px', border: 'none', textAlign: 'left',
                        background: active ? '#f0fdfa' : 'white',
                        cursor: 'pointer', transition: 'background .1s',
                        fontFamily: FONT,
                      }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f9fafb' }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'white' }}
                    >
                      <span style={{ fontSize: 18, flexShrink: 0 }}>{lng.flag}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 500,
                          color: active ? '#0d9488' : '#111827',
                        }}>
                          {lng.nativeLabel}
                          {lng.dir === 'rtl' && (
                            <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>RTL</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>
                          {lng.label}
                        </div>
                      </div>
                      {active && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                          stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                          style={{ flexShrink: 0 }}>
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
